import { useEffect, useState } from "react";
import { Plus, Globe, Trash2, Loader2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";

export default function ProjectsPage() {
  const { projects, refresh, setActive, activeId } = useProjects();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [language, setLanguage] = useState("English");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/projects", { name, domain, primary_language: language });
      setOpen(false); setName(""); setDomain(""); setLanguage("English");
      refresh();
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project? This will also delete all linked analyses & plans.")) return;
    await api.delete(`/projects/${id}`).catch(() => {});
    refresh();
  };

  return (
    <DashboardLayout
      eyebrow="Workspaces"
      title="Manage your sites."
      subtitle="One workspace per site or client. Switch contexts from the sidebar."
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-10" data-testid="projects-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-display">Create project</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required
                       className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="projects-new-name" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Domain</Label>
                <Input value={domain} onChange={(e) => setDomain(e.target.value)} required placeholder="mysite.com"
                       className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="projects-new-domain" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Primary language</Label>
                <Input value={language} onChange={(e) => setLanguage(e.target.value)}
                       className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="projects-new-lang" />
              </div>
              {error && <div className="text-sm text-red-400" data-testid="projects-new-error">{error}</div>}
              <DialogFooter>
                <Button type="submit" disabled={loading} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="projects-new-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {projects.length === 0 ? (
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center">
          <Globe className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-display text-xl mb-2">No projects yet</h3>
          <p className="text-zinc-400 mb-6">Add your first site to get a personalized growth plan.</p>
          <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="projects-empty-cta">
            <Link to="/onboarding"><Sparkles className="w-4 h-4 mr-2" /> Run onboarding wizard</Link>
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {projects.map((p) => {
            const isActive = p.id === activeId;
            return (
              <div key={p.id}
                   className={`relative bg-zinc-950 border rounded-sm p-6 hover:-translate-y-1 transition-transform group ${isActive ? "border-[#0055FF]" : "border-zinc-800"}`}
                   data-testid={`project-card-${p.id}`}>
                {isActive && <Badge className="absolute top-4 right-4 bg-[#0055FF]/15 text-[#3377FF] border-[#0055FF]/30 rounded-sm">ACTIVE</Badge>}
                <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded-sm grid place-items-center mb-4 group-hover:border-[#0055FF]/40 transition-colors">
                  <Globe className="w-5 h-5 text-[#0055FF]" />
                </div>
                <h3 className="font-display text-xl font-medium truncate">{p.name}</h3>
                <p className="text-sm text-zinc-500 truncate mt-1">{p.domain}</p>
                {p.industry && <Badge variant="outline" className="mt-3 rounded-sm border-zinc-700 text-zinc-400">{p.industry}</Badge>}
                <div className="mt-6 flex gap-2">
                  {!isActive && (
                    <Button onClick={() => setActive(p.id)} size="sm" variant="outline"
                            className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid={`project-activate-${p.id}`}>
                      Activate
                    </Button>
                  )}
                  <Button onClick={() => remove(p.id)} size="sm" variant="ghost"
                          className="text-zinc-500 hover:text-[#EF4444] hover:bg-zinc-900 rounded-sm ml-auto"
                          data-testid={`project-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
